import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI, userAPI, couponAPI, paymentAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import { load } from '@cashfreepayments/cashfree-js';

function Cart() {
  const navigate = useNavigate();
  const [allCarts, setAllCarts] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({}); // Per-restaurant payment method
  const [loading, setLoading] = useState({});
  const [availableCoupons, setAvailableCoupons] = useState({});
  const [appliedCoupons, setAppliedCoupons] = useState({});
  const [couponCodes, setCouponCodes] = useState({});
  const [showCoupons, setShowCoupons] = useState({});
  const [deliveryFees, setDeliveryFees] = useState({}); // Per-restaurant delivery fee
  const [deliveryDistances, setDeliveryDistances] = useState({}); // Per-restaurant distance
  // BACKEND SINGLE SOURCE OF TRUTH - All financial values from backend
  const [billPreviews, setBillPreviews] = useState({}); // Per-restaurant bill preview from backend

  useEffect(() => {
    const savedCarts = JSON.parse(localStorage.getItem('carts') || '{}');
    setAllCarts(savedCarts);
    fetchAddresses();

    // Initialize payment methods for each restaurant
    const methods = {};
    Object.keys(savedCarts).forEach(restaurantId => {
      methods[restaurantId] = 'cash';
    });
    setPaymentMethods(methods);

    // Fetch available coupons for each restaurant
    Object.keys(savedCarts).forEach(restaurantId => {
      fetchAvailableCoupons(restaurantId);
    });
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await userAPI.getAddresses();
      const addrs = response.data.data.addresses;
      setAddresses(addrs);
      const defaultAddr = addrs.find(a => a.isDefault) || addrs[0];
      setSelectedAddress(defaultAddr);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    }
  };

  const fetchAvailableCoupons = async (restaurantId) => {
    try {
      const response = await couponAPI.getRestaurantCoupons(restaurantId);
      setAvailableCoupons(prev => ({
        ...prev,
        [restaurantId]: response.data.data.coupons || []
      }));
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };

  // Fetch delivery fee for a restaurant based on selected address
  const fetchDeliveryFee = useCallback(async (restaurantId, address) => {
    try {
      const lat = address?.coordinates?.lat;
      const lng = address?.coordinates?.lng;

      const response = await orderAPI.getDeliveryFee(restaurantId, lat, lng);
      const data = response.data.data;

      setDeliveryFees(prev => ({
        ...prev,
        [restaurantId]: data.deliveryFee
      }));

      setDeliveryDistances(prev => ({
        ...prev,
        [restaurantId]: data.distance
      }));
    } catch (error) {
      console.error('Failed to fetch delivery fee:', error);
      // Use default fee on error
      setDeliveryFees(prev => ({
        ...prev,
        [restaurantId]: 20 // Default base fee
      }));
    }
  }, []);

  // Fetch cart preview from backend - SINGLE SOURCE OF TRUTH for all financial calculations
  // Returns: subtotal, deliveryFee, platformFee, GST breakdown, discount, total, grandTotal
  const fetchCartPreview = useCallback(async (restaurantId, address, cart, couponCode = null) => {
    if (!cart || cart.length === 0) return;

    try {
      const lat = address?.coordinates?.lat;
      const lng = address?.coordinates?.lng;

      const response = await orderAPI.getCartPreview({
        restaurantId,
        items: cart.map(item => ({
          menuItemId: item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        lat,
        lng,
        couponCode
      });

      const data = response.data.data;

      // Store all backend-calculated values (SINGLE SOURCE OF TRUTH)
      setBillPreviews(prev => ({
        ...prev,
        [restaurantId]: {
          subtotal: data.subtotal || 0,
          deliveryFee: data.deliveryFee || 0,
          effectiveDeliveryFee: data.effectiveDeliveryFee || data.deliveryFee || 0,
          platformFee: data.platformFee || 0,
          foodGst: data.foodGst || 0,
          platformGst: data.platformGst || 0,
          deliveryFeeGst: data.deliveryFeeGst || 0,
          deliveryGstRate: data.taxRates?.deliveryGstRate ?? 18,
          totalGst: data.totalGst || 0,
          discount: data.effectiveDiscount || data.discount || 0,
          total: data.total || 0,
          grandTotal: data.grandTotal || data.total || 0,
          roundOff: data.roundOff || 0,
          // Delivery info comes nested in backend response
          distance: data.delivery?.distanceKm || 0,
          isOutOfRange: data.delivery?.isOutOfRange || false,
          // Surge pricing info
          surge: data.surge?.applied ? {
            isActive: true,
            multiplier: data.surge.multiplier,
            reason: 'High demand in your area'
          } : null
        }
      }));

      // Also update legacy delivery fee/distance state for backwards compatibility
      setDeliveryFees(prev => ({
        ...prev,
        [restaurantId]: data.deliveryFee || 0
      }));

      setDeliveryDistances(prev => ({
        ...prev,
        [restaurantId]: data.distance || 0
      }));

      console.log(`📊 Cart preview loaded for ${restaurantId}:`, data);
    } catch (error) {
      console.error('Failed to fetch cart preview:', error);
      // Fallback to delivery fee only API
      fetchDeliveryFee(restaurantId, address);
    }
  }, [fetchDeliveryFee]);

  // Fetch cart previews for all restaurants when address or cart changes
  useEffect(() => {
    if (selectedAddress) {
      const restaurantIds = Object.keys(allCarts).filter(id => allCarts[id]?.length > 0);
      restaurantIds.forEach(restaurantId => {
        const couponCode = appliedCoupons[restaurantId]?.code || null;
        fetchCartPreview(restaurantId, selectedAddress, allCarts[restaurantId], couponCode);
      });
    }
  }, [selectedAddress, allCarts, appliedCoupons, fetchCartPreview]);

  // Helper to get bill values for a restaurant (from backend preview or fallback)
  const getBillValues = (restaurantId) => {
    const preview = billPreviews[restaurantId];
    if (preview) {
      return preview;
    }
    // Fallback: calculate locally (only when backend preview not available)
    const cart = allCarts[restaurantId] || [];
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const deliveryFee = deliveryFees[restaurantId] ?? 20;
    return {
      subtotal,
      deliveryFee,
      effectiveDeliveryFee: deliveryFee,
      platformFee: 0,
      foodGst: 0,
      platformGst: 0,
      deliveryFeeGst: 0,
      deliveryGstRate: 18,
      totalGst: 0,
      discount: 0,
      total: subtotal + deliveryFee,
      grandTotal: subtotal + deliveryFee,
      roundOff: 0,
      distance: deliveryDistances[restaurantId] || 0,
      isOutOfRange: false
    };
  };

  // Helper to get delivery fee for a restaurant (with fallback)
  const getDeliveryFee = (restaurantId) => {
    return billPreviews[restaurantId]?.deliveryFee ?? deliveryFees[restaurantId] ?? 20;
  };

  const updateQuantity = (restaurantId, itemId, delta) => {
    const updatedCart = allCarts[restaurantId].map(item =>
      item._id === itemId
        ? { ...item, quantity: Math.max(1, item.quantity + delta) }
        : item
    );

    const newCarts = { ...allCarts, [restaurantId]: updatedCart };
    setAllCarts(newCarts);
    localStorage.setItem('carts', JSON.stringify(newCarts));
  };

  const removeItem = (restaurantId, itemId) => {
    const updatedCart = allCarts[restaurantId].filter(item => item._id !== itemId);

    if (updatedCart.length === 0) {
      // Remove entire restaurant cart if empty
      const newCarts = { ...allCarts };
      delete newCarts[restaurantId];
      setAllCarts(newCarts);
      localStorage.setItem('carts', JSON.stringify(newCarts));
    } else {
      const newCarts = { ...allCarts, [restaurantId]: updatedCart };
      setAllCarts(newCarts);
      localStorage.setItem('carts', JSON.stringify(newCarts));
    }
  };

  const applyCoupon = async (restaurantId) => {
    const code = couponCodes[restaurantId];
    if (!code || !code.trim()) {
      alert('Please enter a coupon code');
      return;
    }

    const cart = allCarts[restaurantId];
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    try {
      const response = await couponAPI.validate({
        code: code,
        restaurantId: restaurantId,
        orderValue: subtotal
      });

      setAppliedCoupons(prev => ({
        ...prev,
        [restaurantId]: response.data.data.coupon
      }));
      alert(`✅ Coupon applied! You saved ₹${response.data.data.discount}`);
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to apply coupon'));
      setAppliedCoupons(prev => {
        const updated = { ...prev };
        delete updated[restaurantId];
        return updated;
      });
    }
  };

  const removeCoupon = (restaurantId) => {
    setCouponCodes(prev => {
      const updated = { ...prev };
      delete updated[restaurantId];
      return updated;
    });
    setAppliedCoupons(prev => {
      const updated = { ...prev };
      delete updated[restaurantId];
      return updated;
    });
  };

  const placeOrder = async (restaurantId) => {
    if (!selectedAddress) {
      alert('Please select a delivery address');
      return;
    }

    if (!selectedAddress.coordinates || !selectedAddress.coordinates.lat || !selectedAddress.coordinates.lng) {
      alert('⚠️ The selected address does not have GPS coordinates.\n\nPlease edit the address in your profile and use "Detect Location" to add GPS coordinates for delivery tracking.');
      return;
    }

    setLoading(prev => ({ ...prev, [restaurantId]: true }));

    try {
      const cart = allCarts[restaurantId];
      const appliedCoupon = appliedCoupons[restaurantId];
      const paymentMethod = paymentMethods[restaurantId];

      const orderData = {
        restaurantId: restaurantId,
        items: cart.map(item => ({
          menuItemId: item._id,
          quantity: item.quantity
        })),
        deliveryAddress: {
          street: selectedAddress.street,
          city: selectedAddress.city,
          state: selectedAddress.state,
          zipCode: selectedAddress.zipCode,
          coordinates: {
            lat: selectedAddress.coordinates.lat,
            lng: selectedAddress.coordinates.lng
          }
        },
        paymentMethod: paymentMethod,
        couponCode: appliedCoupon ? appliedCoupon.code : null
      };

      // Create the order first
      const response = await orderAPI.create(orderData);
      const orderId = response.data.data.order._id;

      // If payment method is cash, navigate directly to order page
      if (paymentMethod === 'cash') {
        // Remove this restaurant's cart from storage
        const newCarts = { ...allCarts };
        delete newCarts[restaurantId];
        setAllCarts(newCarts);
        localStorage.setItem('carts', JSON.stringify(newCarts));

        navigate(`/user/order/${orderId}`);
      } else {
        // For card/upi, initiate Cashfree payment
        // Use backend-calculated total from the order response (SINGLE SOURCE OF TRUTH)
        const orderData = response.data.data.order;
        const total = orderData.pricing?.grandTotal || orderData.pricing?.total || orderData.total || 0;

        // Create payment session with backend-calculated amount
        const paymentResponse = await paymentAPI.createOrderPayment({
          orderId: orderId,
          amount: total
        });

        // Initialize Cashfree
        const cashfree = await load({
          mode: process.env.REACT_APP_CASHFREE_ENV === 'PROD' ? 'production' : 'sandbox'
        });

        // Checkout options
        const checkoutOptions = {
          paymentSessionId: paymentResponse.data.data.paymentSessionId,
          returnUrl: `${window.location.origin}/user/order/${orderId}?payment_status=success`
        };

        // Open Cashfree checkout
        cashfree.checkout(checkoutOptions).then(() => {
          // Payment modal opened successfully
          console.log('Payment checkout opened');

          // Remove this restaurant's cart from storage
          const newCarts = { ...allCarts };
          delete newCarts[restaurantId];
          setAllCarts(newCarts);
          localStorage.setItem('carts', JSON.stringify(newCarts));
        });
      }
    } catch (error) {
      console.error('Order/Payment error:', error);
      alert('Failed to place order: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(prev => ({ ...prev, [restaurantId]: false }));
    }
  };

  // Discount is now always from backend (Single Source of Truth)
  // The bill.discount value includes all coupon logic calculated server-side

  const restaurantIds = Object.keys(allCarts).filter(id => allCarts[id].length > 0);

  if (restaurantIds.length === 0) {
    return (
      <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
        <Navbar />
        <div className="container">
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '120px', marginBottom: 'var(--space-lg)' }}>🛒</div>
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              color: 'var(--gray-700)',
              marginBottom: 'var(--space-md)'
            }}>
              Your cart is empty
            </h2>
            <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-xl)', fontSize: 'var(--font-size-lg)' }}>
              Time to fill it up with delicious food!
            </p>
            <button onClick={() => navigate('/user/home')} className="btn btn-primary btn-lg">
              🏪 Browse Restaurants
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            🛒 Your Carts
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            You have orders from {restaurantIds.length} {restaurantIds.length === 1 ? 'restaurant' : 'restaurants'}
          </p>
        </div>

        {/* Delivery Address Selection - Shared across all carts */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-2xl)' }}>
          <h2 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)'
          }}>
            📍 Delivery Address (for all orders)
          </h2>
          {addresses.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 'var(--space-xl)',
              background: 'rgba(255, 107, 107, 0.1)',
              borderRadius: 'var(--radius-md)',
              border: '2px solid rgba(255, 107, 107, 0.2)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: 'var(--space-md)' }}>⚠️</div>
              <p style={{ color: 'var(--danger)', marginBottom: 'var(--space-md)', fontWeight: '600' }}>
                No delivery address found
              </p>
              <button onClick={() => navigate('/user/profile')} className="btn btn-primary">
                ➕ Add Address
              </button>
            </div>
          ) : (
            <>
              {addresses.map(addr => (
                <div key={addr._id} style={{
                  marginBottom: 'var(--space-md)',
                  padding: 'var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  border: selectedAddress?._id === addr._id
                    ? '3px solid var(--primary)'
                    : '2px solid var(--gray-300)',
                  background: selectedAddress?._id === addr._id
                    ? 'rgba(255, 107, 107, 0.05)'
                    : 'white',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)'
                }}
                onClick={() => setSelectedAddress(addr)}
                >
                  <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)' }}>
                    <input
                      type="radio"
                      checked={selectedAddress?._id === addr._id}
                      onChange={() => setSelectedAddress(addr)}
                      style={{ marginTop: '4px' }}
                    />
                    <div>
                      <strong style={{ fontSize: 'var(--font-size-lg)', color: 'var(--gray-900)' }}>
                        {addr.label}
                      </strong>
                      <p style={{ margin: '4px 0 0 0', color: 'var(--gray-600)' }}>
                        {addr.street}, {addr.city}, {addr.state} - {addr.zipCode}
                      </p>
                    </div>
                  </label>
                </div>
              ))}

              {selectedAddress && (
                <div style={{
                  marginTop: 'var(--space-lg)',
                  padding: 'var(--space-md)',
                  background: (selectedAddress.coordinates?.lat && selectedAddress.coordinates?.lng)
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(255, 184, 77, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  border: (selectedAddress.coordinates?.lat && selectedAddress.coordinates?.lng)
                    ? '2px solid rgba(16, 185, 129, 0.3)'
                    : '2px solid rgba(255, 184, 77, 0.3)'
                }}>
                  <p style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: '600',
                    color: 'var(--gray-700)',
                    margin: 0,
                    marginBottom: 'var(--space-xs)'
                  }}>
                    🗺️ GPS Coordinates for Delivery Navigation
                  </p>
                  {selectedAddress.coordinates?.lat && selectedAddress.coordinates?.lng ? (
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--success)',
                      margin: 0,
                      fontWeight: '600'
                    }}>
                      ✅ GPS coordinates available ({selectedAddress.coordinates.lat.toFixed(6)}, {selectedAddress.coordinates.lng.toFixed(6)})
                    </p>
                  ) : (
                    <>
                      <p style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--warning)',
                        margin: 0,
                        marginBottom: 'var(--space-sm)',
                        fontWeight: '600'
                      }}>
                        ⚠️ No GPS coordinates for this address
                      </p>
                      <button
                        onClick={() => navigate('/user/profile')}
                        className="btn btn-sm btn-secondary"
                      >
                        📍 Add Coordinates in Profile
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Restaurant Carts */}
        {restaurantIds.map(restaurantId => {
          const cart = allCarts[restaurantId];
          const restaurantName = cart[0]?.restaurantName || 'Restaurant';
          // Use backend-calculated values (Single Source of Truth)
          const bill = getBillValues(restaurantId);
          const subtotal = bill.subtotal;
          const deliveryFee = bill.effectiveDeliveryFee;
          const distance = bill.distance;
          const tax = bill.totalGst;
          const platformFee = bill.platformFee;
          const discount = bill.discount; // From backend only (Single Source of Truth)
          const total = bill.grandTotal;
          const appliedCoupon = appliedCoupons[restaurantId];
          const coupons = availableCoupons[restaurantId] || [];
          const surge = bill.surge;

          return (
            <div key={restaurantId} className="card fade-in" style={{ marginBottom: 'var(--space-2xl)' }}>
              {/* Restaurant Header */}
              <div style={{
                marginBottom: 'var(--space-xl)',
                padding: 'var(--space-lg)',
                background: 'var(--gradient-secondary)',
                borderRadius: 'var(--radius-md)',
                color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: 'var(--radius-md)',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    🏪
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      margin: 0,
                      marginBottom: 'var(--space-xs)',
                      opacity: 0.9
                    }}>
                      Ordering from
                    </p>
                    <h3 style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      margin: 0,
                      color: 'white'
                    }}>
                      {restaurantName}
                    </h3>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      margin: 0,
                      marginBottom: 'var(--space-xs)',
                      opacity: 0.9
                    }}>
                      Cart Total
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      margin: 0,
                      color: 'white'
                    }}>
                      ₹{total.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-xl)' }}>
                <div>
                  {/* Order Items */}
                  <h3 style={{
                    fontSize: 'var(--font-size-xl)',
                    fontWeight: '700',
                    marginBottom: 'var(--space-lg)',
                    color: 'var(--gray-900)'
                  }}>
                    📦 Items
                  </h3>
                  {cart.map(item => (
                    <div key={item._id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'var(--space-lg)',
                      marginBottom: 'var(--space-md)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--gray-100)',
                      transition: 'all var(--transition-base)'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: 'var(--font-size-lg)',
                          fontWeight: '700',
                          color: 'var(--gray-900)',
                          marginBottom: 'var(--space-xs)'
                        }}>
                          {item.name}
                        </h4>
                        <p style={{
                          fontSize: 'var(--font-size-xl)',
                          fontWeight: '700',
                          color: 'var(--primary)',
                          margin: 0
                        }}>
                          ₹{item.price}
                        </p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-sm)',
                          background: 'white',
                          padding: 'var(--space-xs)',
                          borderRadius: 'var(--radius-md)',
                          border: '2px solid var(--gray-300)'
                        }}>
                          <button
                            onClick={() => updateQuantity(restaurantId, item._id, -1)}
                            className="btn btn-sm"
                            style={{
                              background: 'var(--gray-200)',
                              color: 'var(--gray-800)',
                              minWidth: '36px'
                            }}
                          >
                            ➖
                          </button>
                          <span style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: '700',
                            minWidth: '30px',
                            textAlign: 'center'
                          }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(restaurantId, item._id, 1)}
                            className="btn btn-sm"
                            style={{
                              background: 'var(--gradient-secondary)',
                              color: 'white',
                              minWidth: '36px'
                            }}
                          >
                            ➕
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(restaurantId, item._id)}
                          className="btn btn-danger btn-sm"
                        >
                          🗑️ Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Payment Method */}
                  <div style={{ marginTop: 'var(--space-xl)' }}>
                    <h3 style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-lg)',
                      color: 'var(--gray-900)'
                    }}>
                      💳 Payment Method
                    </h3>
                    <select
                      value={paymentMethods[restaurantId] || 'cash'}
                      onChange={(e) => setPaymentMethods(prev => ({
                        ...prev,
                        [restaurantId]: e.target.value
                      }))}
                      style={{
                        width: '100%',
                        padding: 'var(--space-md)',
                        fontSize: 'var(--font-size-lg)',
                        borderRadius: 'var(--radius-md)',
                        border: '2px solid var(--gray-300)',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="cash">💵 Cash on Delivery</option>
                      <option value="card">💳 Credit/Debit Card</option>
                      <option value="upi">📱 UPI</option>
                    </select>
                  </div>
                </div>

                {/* Bill Summary */}
                <div>
                  <div style={{
                    background: 'var(--gradient-dark)',
                    color: 'white',
                    padding: 'var(--space-lg)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <h3 style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-lg)',
                      color: 'white'
                    }}>
                      💰 Bill Summary
                    </h3>
                    <div style={{
                      padding: 'var(--space-md) 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-sm)',
                        fontSize: 'var(--font-size-base)',
                        opacity: 0.9
                      }}>
                        <span>Subtotal</span>
                        <span>₹{subtotal.toFixed(2)}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-sm)',
                        fontSize: 'var(--font-size-base)',
                        opacity: 0.9
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          Delivery Fee
                          {distance && (
                            <span style={{
                              fontSize: 'var(--font-size-xs)',
                              background: 'rgba(255,255,255,0.2)',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              📍 {distance} km
                            </span>
                          )}
                        </span>
                        <span>₹{deliveryFee.toFixed(2)}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 'var(--font-size-base)',
                        opacity: 0.9
                      }}>
                        <span>GST</span>
                        <span>₹{tax.toFixed(2)}</span>
                      </div>
                      {platformFee > 0 && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: 'var(--space-sm)',
                          fontSize: 'var(--font-size-base)',
                          opacity: 0.9
                        }}>
                          <span>Platform Fee</span>
                          <span>₹{platformFee.toFixed(2)}</span>
                        </div>
                      )}
                      {surge && surge.isActive && (
                        <div style={{
                          marginTop: 'var(--space-sm)',
                          padding: 'var(--space-xs) var(--space-sm)',
                          background: 'rgba(255, 184, 77, 0.3)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          color: '#FFB84D'
                        }}>
                          ⚡ Surge pricing active ({surge.multiplier}x) - {surge.reason || 'High demand'}
                        </div>
                      )}
                    </div>

                    {/* Coupon Section */}
                    <div style={{
                      marginTop: 'var(--space-lg)',
                      padding: 'var(--space-md)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-sm)'
                      }}>
                        <h4 style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600',
                          color: 'white',
                          opacity: 0.9,
                          margin: 0
                        }}>
                          🎫 Have a Coupon?
                        </h4>
                        {coupons.length > 0 && !appliedCoupon && (
                          <button
                            onClick={() => setShowCoupons(prev => ({
                              ...prev,
                              [restaurantId]: !prev[restaurantId]
                            }))}
                            style={{
                              padding: 'var(--space-xs) var(--space-sm)',
                              background: 'rgba(255, 255, 255, 0.2)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '600'
                            }}
                          >
                            {showCoupons[restaurantId] ? '✕ Hide' : `🎁 ${coupons.length} Available`}
                          </button>
                        )}
                      </div>

                      {!appliedCoupon ? (
                        <>
                          <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: showCoupons[restaurantId] ? 'var(--space-md)' : 0 }}>
                            <input
                              type="text"
                              value={couponCodes[restaurantId] || ''}
                              onChange={(e) => setCouponCodes(prev => ({
                                ...prev,
                                [restaurantId]: e.target.value.toUpperCase()
                              }))}
                              placeholder="Enter coupon code"
                              style={{
                                flex: 1,
                                padding: 'var(--space-sm)',
                                borderRadius: 'var(--radius-sm)',
                                border: 'none',
                                fontSize: 'var(--font-size-sm)',
                                textTransform: 'uppercase'
                              }}
                            />
                            <button
                              onClick={() => applyCoupon(restaurantId)}
                              disabled={!couponCodes[restaurantId]?.trim()}
                              style={{
                                padding: 'var(--space-sm) var(--space-md)',
                                background: 'var(--success)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: '600',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              ✓ Apply
                            </button>
                          </div>

                          {showCoupons[restaurantId] && coupons.length > 0 && (
                            <div style={{
                              marginTop: 'var(--space-md)',
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {coupons.map((coupon) => {
                                const isValid = subtotal >= coupon.minOrderValue;
                                return (
                                  <div
                                    key={coupon._id}
                                    onClick={() => {
                                      if (isValid) {
                                        setCouponCodes(prev => ({
                                          ...prev,
                                          [restaurantId]: coupon.code
                                        }));
                                        setShowCoupons(prev => ({
                                          ...prev,
                                          [restaurantId]: false
                                        }));
                                        setTimeout(() => applyCoupon(restaurantId), 100);
                                      }
                                    }}
                                    style={{
                                      padding: 'var(--space-sm)',
                                      marginBottom: 'var(--space-sm)',
                                      background: isValid ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.5)',
                                      borderRadius: 'var(--radius-sm)',
                                      cursor: isValid ? 'pointer' : 'not-allowed',
                                      border: '2px dashed ' + (isValid ? 'var(--success)' : 'var(--gray-400)'),
                                      opacity: isValid ? 1 : 0.6
                                    }}
                                  >
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: 'var(--space-xs)'
                                    }}>
                                      <span style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: '700',
                                        color: 'var(--gray-900)',
                                        fontFamily: 'monospace'
                                      }}>
                                        {coupon.code}
                                      </span>
                                      <span style={{
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: '700',
                                        color: 'var(--success)'
                                      }}>
                                        {coupon.discountType === 'percentage' ? `${coupon.discountValue}% OFF` : `₹${coupon.discountValue} OFF`}
                                      </span>
                                    </div>
                                    <p style={{
                                      fontSize: 'var(--font-size-xs)',
                                      color: 'var(--gray-700)',
                                      margin: 0,
                                      marginBottom: 'var(--space-xs)'
                                    }}>
                                      {coupon.description}
                                    </p>
                                    <div style={{
                                      fontSize: 'var(--font-size-xs)',
                                      color: isValid ? 'var(--gray-600)' : 'var(--danger)',
                                      fontWeight: '600'
                                    }}>
                                      {isValid ? '✅ Valid' : `⚠️ Min: ₹${coupon.minOrderValue}`}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: 'var(--space-sm)',
                          background: 'rgba(16, 185, 129, 0.2)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(16, 185, 129, 0.4)'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: '700',
                              color: 'var(--success)',
                              fontFamily: 'monospace'
                            }}>
                              {appliedCoupon.code}
                            </div>
                            <div style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'white',
                              opacity: 0.8,
                              marginTop: '2px'
                            }}>
                              {appliedCoupon.description}
                            </div>
                          </div>
                          <button
                            onClick={() => removeCoupon(restaurantId)}
                            style={{
                              padding: 'var(--space-xs) var(--space-sm)',
                              background: 'rgba(255, 107, 107, 0.8)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '600'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>

                    {discount > 0 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 'var(--space-md)',
                        fontSize: 'var(--font-size-lg)',
                        color: 'var(--success)',
                        fontWeight: '700'
                      }}>
                        <span>🎉 Discount</span>
                        <span>- ₹{discount.toFixed(2)}</span>
                      </div>
                    )}

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'var(--space-md)',
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      paddingTop: 'var(--space-md)',
                      borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <span>Total</span>
                      <span>₹{total.toFixed(2)}</span>
                    </div>

                    <button
                      onClick={() => placeOrder(restaurantId)}
                      className="btn btn-lg"
                      style={{
                        width: '100%',
                        marginTop: 'var(--space-xl)',
                        background: 'white',
                        color: 'var(--primary)',
                        fontWeight: '700',
                        fontSize: 'var(--font-size-lg)'
                      }}
                      disabled={loading[restaurantId] || addresses.length === 0}
                    >
                      {loading[restaurantId] ? '🔄 Placing Order...' : addresses.length === 0 ? '⚠️ Add Address First' : '🚀 Place Order'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Cart;
